import React, { useState } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Typography,
  TextField,
  IconButton,
  Button,
  FormHelperText,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useTranslate, useInput, useNotify } from 'react-admin';
import { Gavel, Add, Delete, ContentCopy, SwapHoriz } from '@mui/icons-material';
import * as OpenCC from 'opencc-js';

interface PromotionTermsManagerProps {
  formData?: any;
}

export const PromotionTermsManager: React.FC<PromotionTermsManagerProps> = ({ formData }) => {
  const [currentLang, setCurrentLang] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const translate = useTranslate();
  const termsInput = useInput({ source: 'terms' });
  const notify = useNotify();

  const languages = [
    { code: 'en', name: 'English' },
    { code: 'zh-TW', name: '繁體中文' },
    { code: 'zh-CN', name: '简体中文' }
  ];

  // Get terms from form data
  const terms = React.useMemo(() =>
    termsInput.field.value || [],
    [termsInput.field.value]
  );

  // Initialize terms array if empty
  React.useEffect(() => {
    if (terms.length === 0) {
      termsInput.field.onChange([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setCurrentLang(newValue);
  };

  const handleAddTerm = () => {
    const newTerms = [...terms];
    const newTerm = {
      termText: '',
      displayOrder: terms.length,
      translations: [
        { language: 'en', termText: '' },
        { language: 'zh-TW', termText: '' },
        { language: 'zh-CN', termText: '' }
      ]
    };
    newTerms.push(newTerm);
    termsInput.field.onChange(newTerms);
  };

  const handleRemoveTerm = (index: number) => {
    const newTerms = terms.filter((_: any, i: number) => i !== index);
    // Reorder remaining terms
    newTerms.forEach((term: any, i: number) => {
      term.displayOrder = i;
    });
    termsInput.field.onChange(newTerms);
  };

  const handleTermChange = (termIndex: number, langCode: string, value: string) => {
    const newTerms = [...terms];
    const term = newTerms[termIndex];

    if (langCode === 'en') {
      // Update main termText (English)
      term.termText = value;
    }

    // Update translation
    if (!term.translations) {
      term.translations = [
        { language: 'en', termText: '' },
        { language: 'zh-TW', termText: '' },
        { language: 'zh-CN', termText: '' }
      ];
    }

    const translationIndex = term.translations.findIndex((t: any) => t.language === langCode);
    if (translationIndex !== -1) {
      term.translations[translationIndex].termText = value;
    }

    termsInput.field.onChange(newTerms);
  };

  const handleCopyFromEnglish = () => {
    const newTerms = [...terms];

    newTerms.forEach((term: any) => {
      if (!term.translations) {
        term.translations = [
          { language: 'en', termText: '' },
          { language: 'zh-TW', termText: '' },
          { language: 'zh-CN', termText: '' }
        ];
      }

      const englishTranslation = term.translations.find((t: any) => t.language === 'en');
      const currentLangCode = languages[currentLang].code;
      const currentTranslation = term.translations.find((t: any) => t.language === currentLangCode);

      if (englishTranslation && currentTranslation) {
        currentTranslation.termText = englishTranslation.termText || term.termText;
      }
    });

    termsInput.field.onChange(newTerms);
    notify('Copied from English', { type: 'success' });
  };

  const handleTranslateFromSimplified = () => {
    const converter = OpenCC.Converter({ from: 'cn', to: 'tw' });
    const newTerms = [...terms];

    let hasSimplified = false;

    newTerms.forEach((term: any) => {
      if (!term.translations) return;

      const simplifiedTranslation = term.translations.find((t: any) => t.language === 'zh-CN');
      const traditionalTranslation = term.translations.find((t: any) => t.language === 'zh-TW');

      if (simplifiedTranslation && simplifiedTranslation.termText && traditionalTranslation) {
        traditionalTranslation.termText = converter(simplifiedTranslation.termText);
        hasSimplified = true;
      }
    });

    if (!hasSimplified) {
      notify('Please fill in Simplified Chinese first', { type: 'warning' });
      return;
    }

    termsInput.field.onChange(newTerms);
    notify('Translated from Simplified Chinese', { type: 'success' });
  };

  const handleTranslateFromTraditional = () => {
    const converter = OpenCC.Converter({ from: 'tw', to: 'cn' });
    const newTerms = [...terms];

    let hasTraditional = false;

    newTerms.forEach((term: any) => {
      if (!term.translations) return;

      const traditionalTranslation = term.translations.find((t: any) => t.language === 'zh-TW');
      const simplifiedTranslation = term.translations.find((t: any) => t.language === 'zh-CN');

      if (traditionalTranslation && traditionalTranslation.termText && simplifiedTranslation) {
        simplifiedTranslation.termText = converter(traditionalTranslation.termText);
        hasTraditional = true;
      }
    });

    if (!hasTraditional) {
      notify('Please fill in Traditional Chinese first', { type: 'warning' });
      return;
    }

    termsInput.field.onChange(newTerms);
    notify('Translated from Traditional Chinese', { type: 'success' });
  };

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    const newTerms = [...terms];
    [newTerms[index - 1], newTerms[index]] = [newTerms[index], newTerms[index - 1]];
    // Update display order
    newTerms.forEach((term: any, i: number) => {
      term.displayOrder = i;
    });
    termsInput.field.onChange(newTerms);
  };

  const handleMoveDown = (index: number) => {
    if (index === terms.length - 1) return;
    const newTerms = [...terms];
    [newTerms[index], newTerms[index + 1]] = [newTerms[index + 1], newTerms[index]];
    // Update display order
    newTerms.forEach((term: any, i: number) => {
      term.displayOrder = i;
    });
    termsInput.field.onChange(newTerms);
  };

  const getCurrentTermText = (term: any) => {
    const langCode = languages[currentLang].code;
    if (langCode === 'en') {
      return term.termText || '';
    }
    const translation = term.translations?.find((t: any) => t.language === langCode);
    return translation?.termText || '';
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Gavel sx={{ mr: 1, color: '#F59E0B' }} />
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {translate('resources.promotions.terms.title')}
        </Typography>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {translate('resources.promotions.terms.description')}
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: { xs: 1, sm: 0 }, mb: 2 }}>
        <Tabs
          value={currentLang}
          onChange={handleTabChange}
          variant={isMobile ? "fullWidth" : "standard"}
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            minHeight: { xs: 40, sm: 48 },
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              minWidth: { xs: 0, sm: 90 },
              minHeight: { xs: 40, sm: 48 },
              px: { xs: 1, sm: 2 },
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
            }
          }}
        >
          {languages.map((lang) => (
            <Tab key={lang.code} label={lang.name} />
          ))}
        </Tabs>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {languages[currentLang].code !== 'en' && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<ContentCopy />}
              onClick={handleCopyFromEnglish}
              sx={{
                textTransform: 'none',
                borderColor: '#00C853',
                color: '#00C853',
                '&:hover': {
                  borderColor: '#00A843',
                  backgroundColor: 'rgba(0, 200, 83, 0.08)',
                }
              }}
            >
              {translate('resources.promotions.terms.copyFromEnglish')}
            </Button>
          )}

          {languages[currentLang].code === 'zh-TW' && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<SwapHoriz />}
              onClick={handleTranslateFromSimplified}
              sx={{
                textTransform: 'none',
                borderColor: '#2196F3',
                color: '#2196F3',
                '&:hover': {
                  borderColor: '#1976D2',
                  backgroundColor: 'rgba(33, 150, 243, 0.08)',
                }
              }}
            >
              從簡體中文翻譯
            </Button>
          )}

          {languages[currentLang].code === 'zh-CN' && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<SwapHoriz />}
              onClick={handleTranslateFromTraditional}
              sx={{
                textTransform: 'none',
                borderColor: '#2196F3',
                color: '#2196F3',
                '&:hover': {
                  borderColor: '#1976D2',
                  backgroundColor: 'rgba(33, 150, 243, 0.08)',
                }
              }}
            >
              从繁体中文翻译
            </Button>
          )}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
        {terms.length === 0 && (
          <Box sx={{
            p: 3,
            backgroundColor: '#F3F4F6',
            borderRadius: 1,
            textAlign: 'center',
            border: '1px dashed #D1D5DB'
          }}>
            <Typography variant="body2" color="text.secondary">
              {translate('resources.promotions.terms.noTerms')}
            </Typography>
          </Box>
        )}

        {terms.map((term: any, index: number) => (
          <Box key={index} sx={{
            p: 2,
            backgroundColor: '#FAFAFA',
            borderRadius: 1,
            border: '1px solid #E5E7EB'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <Box sx={{
                minWidth: 32,
                height: 32,
                borderRadius: '50%',
                backgroundColor: '#F59E0B',
                color: '#FFF',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 'bold',
                fontSize: 14
              }}>
                {index + 1}
              </Box>

              <Box sx={{ flex: 1 }}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  value={getCurrentTermText(term)}
                  onChange={(e) => handleTermChange(index, languages[currentLang].code, e.target.value)}
                  placeholder={`Enter term ${index + 1} in ${languages[currentLang].name}`}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#FFFFFF',
                    },
                  }}
                />
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <IconButton
                  size="small"
                  onClick={() => handleMoveUp(index)}
                  disabled={index === 0}
                  sx={{ color: '#6B7280' }}
                >
                  ↑
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => handleMoveDown(index)}
                  disabled={index === terms.length - 1}
                  sx={{ color: '#6B7280' }}
                >
                  ↓
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => handleRemoveTerm(index)}
                  sx={{ color: '#EF4444' }}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          </Box>
        ))}
      </Box>

      <Button
        variant="outlined"
        startIcon={<Add />}
        onClick={handleAddTerm}
        sx={{
          textTransform: 'none',
          borderColor: '#F59E0B',
          color: '#F59E0B',
          '&:hover': {
            borderColor: '#D97706',
            backgroundColor: 'rgba(245, 158, 11, 0.08)',
          }
        }}
      >
        {translate('resources.promotions.terms.addTerm')}
      </Button>

      {languages[currentLang].code !== 'en' && (
        <Box sx={{
          mt: 2,
          p: 2,
          backgroundColor: '#FEF3C7',
          borderRadius: 1,
          border: '1px solid #FCD34D'
        }}>
          <Typography variant="caption" color="text.secondary">
            {translate('resources.promotions.terms.noteOtherLangs')}
          </Typography>
        </Box>
      )}
    </Box>
  );
};
